import { AlertTriangle, CheckCircle, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ImportPreviewResult, ImportSummary } from "@/hooks/use-categories";
import { useImportCategories } from "@/hooks/use-categories";

type ImportState =
  | { stage: "idle" }
  | { stage: "selected"; file: File }
  | { stage: "validating"; file: File }
  | { stage: "preview"; file: File; preview: ImportPreviewResult }
  | { stage: "importing"; file: File }
  | { stage: "success"; summary: ImportSummary }
  | { stage: "error"; message: string };

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkImportDialog({ open, onOpenChange }: BulkImportDialogProps) {
  const [state, setState] = useState<ImportState>({ stage: "idle" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportCategories();

  function handleClose() {
    setState({ stage: "idle" });
    onOpenChange(false);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setState({ stage: "selected", file });
  }

  async function handleValidate() {
    if (state.stage !== "selected") return;
    const { file } = state;
    setState({ stage: "validating", file });

    importMutation.mutate(
      { file, preview: true },
      {
        onSuccess: (data) => {
          const preview = data as ImportPreviewResult;
          setState({ stage: "preview", file, preview });
        },
        onError: (err) => {
          setState({ stage: "error", message: err.message });
        },
      },
    );
  }

  async function handleConfirmImport() {
    if (state.stage !== "preview") return;
    const { file } = state;
    setState({ stage: "importing", file });

    importMutation.mutate(
      { file, preview: false },
      {
        onSuccess: (data) => {
          setState({ stage: "success", summary: data as ImportSummary });
        },
        onError: (err) => {
          setState({ stage: "error", message: err.message });
        },
      },
    );
  }

  function handleReset() {
    setState({ stage: "idle" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk Import Categories</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import or update activity categories. Existing records matched by
            name are updated — nothing is deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* CSV format hint */}
          <div className="rounded-md bg-muted p-3 text-xs font-mono text-muted-foreground">
            Category Name,Job Type Tags,Subtask Name,Active
            <br />
            Report Writing,NMP|AMB|NIA,Preliminary,true
            <br />
            Report Writing,NMP|AMB|NIA,Draft,true
          </div>

          {/* File picker — always visible unless success */}
          {state.stage !== "success" && (
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={state.stage === "validating" || state.stage === "importing"}
              >
                <Upload className="mr-2 h-4 w-4" />
                {state.stage === "idle" ? "Select CSV file" : "Change file"}
              </Button>
              {(state.stage === "selected" ||
                state.stage === "validating" ||
                state.stage === "preview" ||
                state.stage === "importing") && (
                <p className="text-sm text-muted-foreground truncate">{state.file.name}</p>
              )}
            </div>
          )}

          {/* Validation / preview results */}
          {state.stage === "validating" && (
            <p className="text-sm text-muted-foreground">Validating file…</p>
          )}

          {state.stage === "preview" && (
            <div className="space-y-3">
              {state.preview.errors.length > 0 ? (
                <div className="callout callout--warn">
                  <AlertTriangle className="callout__icon w-4 h-4" />
                  <div className="text-sm">
                    <strong className="callout__title">
                      {state.preview.errors.length} validation{" "}
                      {state.preview.errors.length === 1 ? "error" : "errors"}
                    </strong>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      {state.preview.errors.slice(0, 10).map((err) => (
                        <li key={`${err.row}-${err.field}`}>
                          Row {err.row}: {err.field} — {err.message}
                        </li>
                      ))}
                      {state.preview.errors.length > 10 && (
                        <li>…and {state.preview.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-border p-3 text-sm space-y-1">
                  <p className="font-medium">Ready to import</p>
                  <p className="text-muted-foreground">{state.preview.rowCount} rows parsed</p>
                  <p className="text-muted-foreground">
                    {new Set(state.preview.rows.map((r) => r.categoryName)).size} categories
                  </p>
                </div>
              )}
            </div>
          )}

          {state.stage === "importing" && (
            <p className="text-sm text-muted-foreground">Importing…</p>
          )}

          {state.stage === "success" && (
            <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-4 text-sm">
              <CheckCircle className="h-5 w-5 shrink-0 text-green-600 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-green-900">Import complete</p>
                <p className="text-green-800">
                  {state.summary.categoriesCreated} created, {state.summary.categoriesUpdated}{" "}
                  updated
                </p>
                <p className="text-green-800">
                  {state.summary.subtasksCreated} subtasks created, {state.summary.subtasksUpdated}{" "}
                  updated
                </p>
              </div>
            </div>
          )}

          {state.stage === "error" && (
            <div className="callout callout--warn">
              <AlertTriangle className="callout__icon w-4 h-4" />
              <p className="text-sm">{state.message}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {state.stage === "success" ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {state.stage === "error" && (
                <Button type="button" variant="outline" onClick={handleReset}>
                  Try again
                </Button>
              )}
              {state.stage === "selected" && (
                <Button type="button" onClick={handleValidate}>
                  Validate
                </Button>
              )}
              {state.stage === "preview" && state.preview.errors.length === 0 && (
                <Button type="button" onClick={handleConfirmImport}>
                  Import
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
